const TOKEN_KEY = 'paladin-session';

export type TradeMode = 'backtest' | 'paper' | 'live';
/** A built-in code (VCL, SMOG, TC1) or one of the trader's own strategy codes */
export type TradeStrategy = string;

export interface Trade {
  id: number;
  mode: TradeMode;
  strategy: TradeStrategy;
  pair: string;
  traded_on: string;
  session: string | null;
  timeframe: string | null;
  /** Null on trades imported from the Readiness tracker (R result only) */
  entry_price: string | null;
  stop_price: string | null;
  max_run_price: string | null;
  exit_price: string | null;
  max_heat_price: string | null;
  max_heat_r: string | null;
  quantity: string | null;
  direction: 'long' | 'short';
  r_multiple: string;
  max_run_r: string | null;
  source?: 'manual' | 'readiness_import';
  pnl_dollars: number | null;
  context: Record<string, unknown>;
  followed_rules: boolean;
  notes: string | null;
  has_image?: boolean;
  partials: { price: number; qty: number }[];
}

export interface ChartDraft {
  pair: string | null;
  traded_on: string | null;
  session: string | null;
  timeframe: string | null;
  entry_price: number | null;
  stop_price: number | null;
  max_run_price: number | null;
  exit_price: number | null;
  max_heat_price?: number | null;
  partials: { price: number; qty: number | null }[];
  sweep: string | null;
  confidence: 'high' | 'medium' | 'low';
  uncertain: string[];
}

export interface NewTrade {
  mode: TradeMode;
  strategy: TradeStrategy;
  pair: string;
  traded_on: string;
  session?: string | null;
  timeframe?: string | null;
  entry_price: number;
  stop_price: number;
  max_run_price: number;
  exit_price: number;
  max_heat_price?: number | null;
  quantity?: number | null;
  partials?: { price: number; qty: number }[];
  context?: Record<string, unknown>;
  followed_rules: boolean;
  notes?: string;
}

export interface StrategyReadiness {
  backtestDays: number;
  backtestTrades: number;
  backtestExpectancy: number;
  paperTrades: number;
  paperExpectancy: number;
  paperUnlocked: boolean;
  ready: boolean;
  /** Open without the normal path: mods/admins, mod-verified veterans, or an existing live record */
  fastTrack: 'staff' | 'veteran' | 'live_record' | null;
}

export interface ReadinessImport {
  imported: number;
  duplicates: number;
  skipped: { row: number; reason: string }[];
  strategies: string[];
  newStrategies: string[];
  fields: string[];
  modes: Record<string, number>;
}

export interface TradeStats {
  trades: number;
  wins: number;
  winRate: number;
  avgWinnerR: number;
  avgLoserR: number;
  expectancyR: number;
  totalR: number;
  ruleAdherence: number;
  avgMaxRunR: number;
  reachRates: number[];
}

export interface StrategyRefit {
  executed: TradeStats;
  currentTier: string | null;
  tradesUntilRefit: number;
  lastRefit: { trade_count: number; win_rate: string; expectancy_r: string; tier: string | null; created_at: string } | null;
}

export interface TradeSummary {
  refitEvery: number;
  stats: TradeStats;
  byStrategy: Record<TradeStrategy, StrategyRefit>;
}

function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

/** Whether this browser holds an API session */
export const hasSession = () => Boolean(getToken());

/** Store a session handed back by Discord login */
export function adoptSession(token: string) { setToken(token); }

function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable — session lasts for this page only */ }
}

const ROLE_KEY = 'paladin-role';

/** Dev stand-in for Discord OAuth: exchanges a Discord username (and guild role) for a real API session. */
let pendingSignIn: Promise<void> | null = null;

export function signIn(username: string, role?: string): Promise<void> {
  // Parallel requests share one sign-in instead of racing to create sessions
  pendingSignIn ??= doSignIn(username, role).finally(() => { pendingSignIn = null; });
  return pendingSignIn;
}

async function doSignIn(username: string, role?: string): Promise<void> {
  if (role) { try { localStorage.setItem(ROLE_KEY, role); } catch { /* per-page only */ } }
  let savedRole: string | null = null;
  try { savedRole = localStorage.getItem(ROLE_KEY); } catch { /* ignore */ }
  const res = await fetch('/api/auth/dev', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username, role: role ?? savedRole ?? undefined }),
  });
  if (!res.ok) throw new Error('Sign-in failed');
  setToken((await res.json()).session_token);
}

export function signOut() {
  setToken(null);
}

async function send(path: string, init: RequestInit, username?: string): Promise<Response> {
  if (!getToken() && username) await signIn(username);
  const go = () => fetch(`/api${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...init.headers, Authorization: `Bearer ${getToken() ?? ''}` },
  });
  let res = await go();
  // Expired or revoked session in production: back to the landing page to sign in with Discord again
  if (res.status === 401 && import.meta.env.PROD) {
    setToken(null);
    window.location.assign('/');
    throw new Error('Your session expired — sign in again');
  }
  if (res.status === 401 && username) {
    await signIn(username);
    res = await go();
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return res;
}

async function request<T>(path: string, init: RequestInit = {}, username?: string): Promise<T> {
  const res = await send(path, init, username);
  return res.status === 204 ? (undefined as T) : res.json();
}

const query = (params: Record<string, string | number | undefined>) =>
  new URLSearchParams(Object.entries(params).filter((e): e is [string, string | number] => e[1] !== undefined).map(([k, v]) => [k, String(v)])).toString();

/** A routine prompt and how it's answered each day */
export interface RoutineItem { text: string; kind: 'note' | 'strategy' | 'number' }
export interface RoutineBlock { name: string; start: string | null; end: string | null; items: RoutineItem[] }
export interface Routine { blocks: RoutineBlock[]; timezone: string | null }

export const routineApi = {
  get: (username: string) => request<{ routine: Routine | null; updated_at: string | null }>('/routine', {}, username),
  save: (username: string, routine: Routine) =>
    request<{ routine: Routine }>('/routine', { method: 'PUT', body: JSON.stringify(routine) }, username).then(r => r.routine),
  /** The day's answers, keyed "blockIndex.itemIndex" */
  day: (username: string, date: string) => request<{ answers: Record<string, string> }>(`/routine/day?date=${date}`, {}, username).then(r => r.answers),
  saveDay: (username: string, date: string, answers: Record<string, string>) =>
    request<void>('/routine/day', { method: 'PUT', body: JSON.stringify({ date, answers }) }, username),
};

export type Tier = 'steady' | 'push' | 'max';
export interface EngineInputs {
  strategy: string;
  sample: number;
  ownData: boolean;
  plannedStreak: number;
  worstStreak: number;
  paper: TradeStats;
  live: TradeStats;
  liveRecent20Expectancy: number;
  currentLosingStreak: number;
  currentDrawdownR: number;
  worstDrawdownR: number;
  riskPerContract: number | null;
  commonPair: string | null;
  payouts: number;
}
export interface TierChoice {
  strategy: string; tier: Tier; firm_id: string; plan_id: string;
  account_size: number; drawdown: string; risk_per_trade: string; chosen_at: string;
}

export const engineApi = {
  get: (username: string, strategy: string) =>
    request<{ ready: boolean; inputs: EngineInputs; choice: TierChoice | null }>(`/engine?strategy=${encodeURIComponent(strategy)}`, {}, username),
  choices: (username: string) => request<{ choices: TierChoice[] }>('/engine/choices', {}, username).then(r => r.choices),
  choose: (username: string, c: { strategy: string; tier: Tier; firm_id: string; plan_id: string; account_size: number; drawdown: number; risk_per_trade: number }) =>
    request<{ choice: TierChoice }>('/engine/choice', { method: 'PUT', body: JSON.stringify(c) }, username).then(r => r.choice),
};

export interface CatalogFirm {
  id: string;
  data: import('../types').PropFirm;
  source_urls: string[];
  verified_on: string | null;
  updated_by: string | null;
  updated_at: string;
}

export interface FirmChange {
  id: number; firm_id: string; firm_name: string | null;
  before: import('../types').PropFirm | null; after: import('../types').PropFirm;
  changed_by: string | null; note: string | null; changed_at: string;
}

export const firmCatalogApi = {
  list: (username: string) => request<{ firms: CatalogFirm[] }>('/firm-catalog', {}, username).then(r => r.firms),
  save: (username: string, id: string, body: { data: import('../types').PropFirm; source_urls: string[]; verified_on: string; note?: string }) =>
    request<{ firm: CatalogFirm }>(`/firm-catalog/${id}`, { method: 'PUT', body: JSON.stringify(body) }, username).then(r => r.firm),
  log: (username: string) => request<{ log: FirmChange[] }>('/firm-catalog/log', {}, username).then(r => r.log),
};

export interface Member { id: string; username: string; avatar_url: string | null; roles: string[]; trades: number; live_trades: number; created_at: string; via_pass_key?: boolean; last_sign_in?: string | null }
export interface PassKey { id: string; name: string; roles: string[]; revoked: boolean; last_used: string | null; created_at: string; sign_ins: number }

export const membersApi = {
  list: (username: string, q = '') => request<{ members: Member[] }>(`/members?q=${encodeURIComponent(q)}`, {}, username).then(r => r.members),
  passKeys: (username: string) => request<{ keys: PassKey[] }>('/members/passkeys', {}, username).then(r => r.keys),
  revokePassKey: (username: string, id: string, revoked: boolean) =>
    request<void>(`/members/passkeys/${id}`, { method: 'PUT', body: JSON.stringify({ revoked }) }, username),
  setVeteran: (username: string, id: string, veteran: boolean, note?: string) =>
    request<{ member: Member }>(`/members/${id}/veteran`, { method: 'PUT', body: JSON.stringify({ veteran, note }) }, username).then(r => r.member),
};

export const progressApi = {
  list: (username: string) => request<{ done: string[]; values: Record<string, string> }>('/progress', {}, username),
  set: (username: string, key: string, done: boolean, value?: string) =>
    request<void>(`/progress/${key}`, { method: 'PUT', body: JSON.stringify({ done, value }) }, username),
};

export const tradesApi = {
  list: (username: string, f: { strategy?: TradeStrategy; mode?: TradeMode; limit?: number } = {}) =>
    request<{ trades: Trade[] }>(`/trades?${query({ limit: f.limit ?? 200, strategy: f.strategy, mode: f.mode })}`, {}, username).then(r => r.trades),
  stats: (username: string, f: { strategy?: TradeStrategy; mode?: TradeMode } = {}) =>
    request<TradeSummary>(`/trades/stats?${query(f)}`, {}, username),
  /** The Master Trade Log CSV exported from the Readiness Progression tracker */
  importReadiness: (username: string, csv: File) =>
    send('/trades/import', { method: 'POST', body: csv, headers: { 'content-type': 'text/csv' } }, username).then(r => r.json() as Promise<ReadinessImport>),
  readiness: (username: string) =>
    request<{ thresholds: { backtestDays: number; paperTrades: number }; strategies: Record<string, StrategyReadiness> }>('/trades/readiness', {}, username),
  create: (username: string, trade: NewTrade) =>
    request<{ trade: Trade }>('/trades', { method: 'POST', body: JSON.stringify(trade) }, username).then(r => r.trade),
  remove: (username: string, id: number) => request<void>(`/trades/${id}`, { method: 'DELETE' }, username),
  readChartStatus: (username: string) => request<{ enabled: boolean }>('/trades/read-chart', {}, username),
  readChart: (username: string, image: Blob) =>
    send('/trades/read-chart', { method: 'POST', body: image, headers: { 'content-type': image.type, 'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone } }, username)
      .then(r => r.json() as Promise<{ draft: ChartDraft }>).then(r => r.draft),
  uploadImage: (username: string, id: number, image: Blob) =>
    send(`/trades/${id}/image`, { method: 'PUT', body: image, headers: { 'content-type': image.type } }, username).then(() => undefined),
  /** Images need the auth header, so they're fetched as blobs and shown via object URLs */
  imageUrl: (username: string, id: number) =>
    send(`/trades/${id}/image`, {}, username).then(r => r.blob()).then(b => URL.createObjectURL(b)),
};

export const authApi = {
  me: (username: string) => request<{ user_id: number; username: string; roles: string[] }>('/auth/me', {}, username),
};

/* ── Marathon Method (Inner Circle) ── */
export type MarathonCohort = 'M' | 'A' | 'B' | 'C' | 'D';

export interface MarathonAccount {
  id: string;
  label: string;
  cohort: MarathonCohort;
  role: 'master' | 'sub';
  size: number;
  firm: string;
  status: 'eval' | 'funded' | 'breached' | 'closed';
  balance: number;
  peak: number;
  mdd: number;
  floor: number;
  locked: boolean;
  iced: boolean;
  iced_until: string | null;
  room: number;
  target: number | null;
  targetHit: boolean;
  payoutReady: boolean;
  nextPayout: number;
  payouts_taken: number;
  platform_id: string | null;
  payoutLine: number | null;
  dll: number | null;
  /** How the drawdown trails: at each day's close, or with every trade */
  trail: 'eod' | 'intraday';
  /** Days traded and best day before the tracker, for an account brought in under way */
  prior_days?: number;
  prior_best?: number | null;
  /** Best day as a share of profit, against the firm's rule (rule null: firm has none tracked); left = profit still needed to meet it */
  consistency?: { days: number; bestDay: number; profit: number; pct: number | null; rule: number | null; left: number | null };
}

export interface MarathonBoard {
  strategy: TradeStrategy;
  accounts: MarathonAccount[];
  kpis: { accountsOpen: number; fundedCapital: number; fundedBreaches: number; moat: number; taxReserve: number; selfPay: number; grossPayouts: number };
  step: { n: number; title: string; next: string };
  sizing: { strategy: TradeStrategy; streak: number; ownData: boolean; sampleSize: number; smallestDrawdown: number; riskPerTrade: number;
    /** The prescription chosen for this strategy, when there is one; it sets the risk */
    prescription: { tier: string; risk: number } | null };
  rotation: { slots: { label: string; accountIds: string[] }[]; next: { index: number; label: string; firing: string[]; sittingOut: string[] } | null };
  payouts: { id: string; label: string; paid_on: string; amount: string; tax: string; self_pay: string; moat: string }[];
  ledger: { id: string; kind: string; amount: string; note: string | null; created_at: string }[];
  firms: string[];
}

export interface MarathonAccountDetail {
  account: MarathonAccount;
  stats: { trades: number; winRate: number; net: number; tradingDays: number; bestTrade: number; totalPaidOut: number };
  recentFills: { traded_on: string; pnl: string; trade_group: string }[];
  payouts: { paid_on: string; amount: string }[];
}

const post = (username: string, path: string, body?: unknown) =>
  request<unknown>(`/marathon${path}`, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }, username);

export const marathonApi = {
  board: (username: string) => request<MarathonBoard>('/marathon', {}, username),
  account: (username: string, id: string) => request<MarathonAccountDetail>(`/marathon/accounts/${id}`, {}, username),
  setStrategy: (username: string, strategy: TradeStrategy) =>
    request<void>('/marathon/state', { method: 'PUT', body: JSON.stringify({ strategy }) }, username),
  addAccount: (username: string, a: { label: string; cohort: MarathonCohort; role: 'master' | 'sub'; size: number; firm: string; platform_id?: string; paid_from_moat?: boolean;
    dll?: number; trail?: 'eod' | 'intraday';
    existing?: { status: 'eval' | 'funded'; balance: number; threshold?: number; payouts_taken?: number; last_payout_on?: string; cost_paid?: number; days_traded?: number; best_day?: number } }) =>
    post(username, '/accounts', a),
  edit: (username: string, id: string, changes: Record<string, string | number | null>) =>
    request('/marathon/accounts/' + id, { method: 'PATCH', body: JSON.stringify(changes) }, username),
  remove: (username: string, id: string) => request('/marathon/accounts/' + id, { method: 'DELETE' }, username),
  setNumber: (username: string, id: string, platform_id: string | null) =>
    request('/marathon/accounts/' + id, { method: 'PATCH', body: JSON.stringify({ platform_id }) }, username),
  markFunded: (username: string, id: string) => post(username, `/accounts/${id}/funded`),
  breach: (username: string, id: string) => post(username, `/accounts/${id}/breach`),
  close: (username: string, id: string) => post(username, `/accounts/${id}/close`),
  payout: (username: string, id: string, amount?: number) => post(username, `/accounts/${id}/payout`, amount ? { amount } : {}),
  thaw: (username: string, id: string) => post(username, `/accounts/${id}/thaw`),
  trade: (username: string, resultR: number) =>
    request<{ slot: string; pnlPerAccount: number; accounts: string[]; breached: string[] }>('/marathon/trade', { method: 'POST', body: JSON.stringify({ result_r: resultR }) }, username),
  skip: (username: string) => post(username, '/skip'),
};

/* ── Stat fields: catalog picks and custom stats on the log form ── */
export type StatFieldType = 'yesno' | 'choice' | 'multi' | 'number' | 'price' | 'text';
export interface StatFieldDef { key: string; label: string; type: StatFieldType; options: string[]; allowCustom?: boolean; parent?: string }
export interface StatField extends StatFieldDef { id: string; source: 'catalog' | 'custom' }

export const statFieldsApi = {
  list: (username: string) => request<{ catalog: StatFieldDef[]; fields: StatField[] }>('/stat-fields', {}, username),
  addCatalog: (username: string, key: string) =>
    request<{ field: StatField }>('/stat-fields', { method: 'POST', body: JSON.stringify({ catalog_key: key }) }, username),
  addCustom: (username: string, f: { label: string; type: StatFieldType; options: string[] }) =>
    request<{ field: StatField }>('/stat-fields', { method: 'POST', body: JSON.stringify(f) }, username),
  remove: (username: string, id: string) => request<void>(`/stat-fields/${id}`, { method: 'DELETE' }, username),
};

/* ── Strategies: built-ins plus the trader's own ── */
export interface StrategyDef { code: string; name: string; planned_streak: number; builtIn: boolean; id?: string }

export const strategiesApi = {
  list: (username: string) => request<{ strategies: StrategyDef[] }>('/strategies', {}, username).then(r => r.strategies),
  create: (username: string, s: { name: string; code: string }) =>
    request<{ strategy: StrategyDef }>('/strategies', { method: 'POST', body: JSON.stringify(s) }, username).then(r => r.strategy),
  remove: (username: string, id: string) => request<void>(`/strategies/${id}`, { method: 'DELETE' }, username),
};
